<section class="ps-root" aria-labelledby="ps-title">
  <header class="ps-header">
    <span class="ps-header-icon" aria-hidden="true"><i class="ki-filled ki-cloud-add"></i></span>
    <div class="ps-header-text">
      <h2 id="ps-title" class="ps-title">Upload to Public Website</h2>
      <p class="ps-subtitle">Pricing supplement PDF for the IDB public website. One document per trade.</p>
    </div>
  </header>

  @if (!loading() && !loadError() && (canUpload() || supplement())) {
  <div class="ps-flags" role="group" aria-label="Pricing supplement options">
    <label class="kt-label flex items-center gap-2 cursor-pointer">
      <input
        type="checkbox"
        class="kt-checkbox ps-checkbox"
        data-testid="ps-flag-executed"
        [checked]="executedDocsSignatureRemoved"
        [disabled]="flagsDisabled"
        (change)="onFlagChange('executedDocsSignatureRemoved', $any($event.target).checked)"
      />
      <span>Officer used Executed Documents and removed signature page</span>
    </label>
    <label class="kt-label flex items-center gap-2 cursor-pointer">
      <input
        type="checkbox"
        class="kt-checkbox ps-checkbox"
        data-testid="ps-flag-private"
        [checked]="isPrivate"
        [disabled]="flagsDisabled"
        (change)="onFlagChange('isPrivatePricingSupplement', $any($event.target).checked)"
      />
      <span>Private Pricing Supplement (No public)</span>
    </label>
  </div>
  }

  <ng-template #missingFieldsNotice>
    <div class="ps-alert ps-alert-warning ps-blocked" role="alert" data-testid="ps-missing-fields">
      <i class="ki-filled ki-information-2" aria-hidden="true"></i>
      <div class="ps-alert-body">
        <strong>Complete the bond details before uploading the pricing supplement.</strong>
        @if (missingFields().length) {
          <span>The following fields are missing:</span>
          <ul class="ps-missing-list">
            @for (item of missingFields(); track item.field) {
              <li>{{ item.label }}</li>
            }
          </ul>
        }
        <span class="ps-muted">Fill them in and save the bond to continue.</span>
      </div>
    </div>
  </ng-template>

  <div class="ps-body">
  @if (loading()) {
    <div class="ps-card ps-state" role="status" data-testid="ps-loading">
      <span class="ps-spinner" aria-hidden="true"></span>
      <span>Loading pricing supplement…</span>
    </div>
  } @else if (loadError()) {
    <div class="ps-alert ps-alert-danger" role="alert" data-testid="ps-load-error">
      <i class="ki-filled ki-information-2" aria-hidden="true"></i>
      <div class="ps-alert-body">
        <span>{{ loadError() }}</span>
      </div>
      <button type="button" class="kt-btn kt-btn-sm kt-btn-outline" (click)="load()">Retry</button>
    </div>
  } @else if (!canUpload() && !supplement()) {
    <ng-container [ngTemplateOutlet]="missingFieldsNotice" />
  } @else if (supplement(); as current) {
    <!-- Summary -->
    <article class="ps-card" data-testid="ps-summary">
      <div class="ps-summary-head">
        <span class="ps-doc-icon" aria-hidden="true"><i class="ki-filled ki-document"></i></span>
        <div class="ps-summary-title">
          <div class="ps-summary-name-row">
            <h3 class="ps-summary-name" [title]="displayName()">{{ displayName() }}</h3>
            <span class="ps-badge" data-tone="warning" data-testid="ps-badge">Pending FIN/BPG approval</span>
          </div>
          <p class="ps-summary-meta">
            @if (current.dateModified) {
              Updated {{ current.dateModified | date: 'MMM d, y, h:mm a' }}@if (current.modifiedBy) { by {{ current.modifiedBy }} }
            } @else if (current.dateCreated) {
              Added {{ current.dateCreated | date: 'MMM d, y, h:mm a' }}@if (current.createdBy) { by {{ current.createdBy }} }
            }
          </p>
        </div>

        <button
          type="button"
          class="kt-btn kt-btn-sm kt-btn-outline ps-remove-btn"
          data-testid="ps-remove"
          [disabled]="deleting()"
          (click)="openDeleteModal()"
        >
          <i class="ki-filled ki-trash" aria-hidden="true"></i>
          Remove
        </button>
      </div>

      <div class="ps-alert ps-alert-info" role="note" data-testid="ps-approval-note">
        <i class="ki-filled ki-information-2" aria-hidden="true"></i>
        <div class="ps-alert-body">Document uploaded in EzShare but not yet public until approved by FIN/BPG.</div>
      </div>

      <dl class="ps-rows">
        <div class="ps-row">
          <dt>Public link</dt>
          <dd>
            @if (current.publicLink) {
              <a class="ps-link" target="_blank" rel="noopener noreferrer" data-testid="ps-public-link" [href]="current.publicLink">{{ current.publicLink }}</a>
              <span class="ps-row-actions">
                <button type="button" class="kt-btn kt-btn-xs kt-btn-icon kt-btn-ghost" aria-label="Copy public link" (click)="copy('public', current.publicLink)">
                  <i class="ki-filled" [class.ki-copy]="copiedField() !== 'public'" [class.ki-check]="copiedField() === 'public'" aria-hidden="true"></i>
                </button>
              </span>
            } @else {
              <span class="ps-muted">Not available</span>
            }
          </dd>
        </div>
        <div class="ps-row">
          <dt>Document ID</dt>
          <dd>
            <code class="ps-code" data-testid="ps-document-id">{{ current.ezShareDocumentId }}</code>
            <span class="ps-row-actions">
              <button type="button" class="kt-btn kt-btn-xs kt-btn-icon kt-btn-ghost" aria-label="Copy document ID" (click)="copy('document', current.ezShareDocumentId)">
                <i class="ki-filled" [class.ki-copy]="copiedField() !== 'document'" [class.ki-check]="copiedField() === 'document'" aria-hidden="true"></i>
              </button>
            </span>
          </dd>
        </div>
        @if (current.internalLink) {
          <div class="ps-row">
            <dt>Internal</dt>
            <dd>
              <a class="ps-link" target="_blank" rel="noopener noreferrer" data-testid="ps-internal-link" [href]="current.internalLink">
                Open in SharePoint <i class="ki-filled ki-exit-right-corner text-xs" aria-hidden="true"></i>
              </a>
            </dd>
          </div>
        }
      </dl>
    </article>
  } @else if (isPrivate) {
    <div class="ps-card ps-state" data-testid="ps-private">
      <i class="ki-filled ki-lock text-lg" aria-hidden="true"></i>
      <span>Marked as private. No pricing supplement will be uploaded to the public website.</span>
    </div>
  } @else {
    <!-- Empty state: primary automatic upload -->
    <div class="ps-card ps-empty" data-testid="ps-empty">
      <app-pricing-supplement-file-picker
        #publishPicker
        inputId="ps-publish-file"
        hint="PDF only · Uploaded to EzShare with the Deal ID as Identifier"
        [disabled]="publishing()"
        (selectionChange)="onSelectionChange($event)"
      />

      @if (publishError()) {
        <div class="ps-alert ps-alert-danger" role="alert" data-testid="ps-publish-error">
          <i class="ki-filled ki-information-2" aria-hidden="true"></i>
          <div class="ps-alert-body">
            <span>{{ publishError() }}</span>
            <button type="button" class="ps-text-action" data-testid="ps-publish-error-link" (click)="openLinkModal()">
              Upload it in EzShare and link it instead →
            </button>
          </div>
        </div>
      }

      @if (selection()) {
        <div class="ps-empty-actions">
          <button
            type="button"
            class="kt-btn kt-btn-primary"
            data-testid="ps-publish"
            [disabled]="publishing()"
            (click)="publish()"
          >
            @if (publishing()) {
              <span class="ps-spinner ps-spinner-sm" aria-hidden="true"></span>
              Uploading…
            } @else {
              <i class="ki-filled ki-cloud-add" aria-hidden="true"></i>
              Upload to EzShare
            }
          </button>
        </div>
      }

      <div class="ps-empty-footer">
        <span class="ps-muted">Already uploaded it in EzShare?</span>
        <button type="button" class="ps-text-action" data-testid="ps-open-link" [disabled]="publishing()" (click)="openLinkModal()">
          Link by document ID →
        </button>
      </div>
    </div>
  }
  </div>
</section>

<!-- Hidden modal toggles (CatalogModalController.open clicks these) -->
<button type="button" class="hidden" aria-hidden="true" tabindex="-1" [attr.data-kt-modal-toggle]="'#' + linkModalId"></button>
<button type="button" class="hidden" aria-hidden="true" tabindex="-1" [attr.data-kt-modal-toggle]="'#' + deleteModalId"></button>

<!-- Link existing EzShare document -->
<div class="kt-modal" data-kt-modal="true" [id]="linkModalId">
  <div class="kt-modal-content max-w-[480px] top-[15%]">
    <div class="kt-modal-header">
      <h3 class="kt-modal-title">Link an EzShare document</h3>
      <button type="button" class="kt-modal-close" aria-label="Close modal" [attr.data-kt-modal-dismiss]="'#' + linkModalId">
        <i class="ki-filled ki-cross" aria-hidden="true"></i>
      </button>
    </div>
    <form class="kt-modal-body ps-modal-body" novalidate (submit)="$event.preventDefault(); submitLink()">
      <p class="ps-muted text-sm">
        Make sure the document is <strong>Public</strong> in EzShare with the trade's <strong>Deal ID</strong> as Identifier.
      </p>

      <div class="ps-field">
        <label class="kt-form-label" for="ps-link-document-id">Document ID <span class="text-destructive">*</span></label>
        <input
          id="ps-link-document-id"
          type="text"
          class="kt-input"
          placeholder="e.g. EZTEST000014-695047144-299"
          autocomplete="off"
          data-testid="ps-link-document-id"
          [value]="linkDocumentId()"
          [disabled]="linking()"
          [attr.aria-invalid]="!!linkDocumentIdError()"
          (input)="linkDocumentId.set($any($event.target).value)"
        />
        @if (linkDocumentIdError()) {
          <span class="ps-field-error" role="alert">{{ linkDocumentIdError() }}</span>
        } @else {
          <span class="ps-field-hint">Found in the document's properties in EzShare.</span>
        }
      </div>

      @if (linkError()) {
        <div class="ps-alert ps-alert-danger" role="alert" data-testid="ps-link-error">
          <i class="ki-filled ki-information-2" aria-hidden="true"></i>
          <div class="ps-alert-body">{{ linkError() }}</div>
        </div>
      }

      <div class="ps-modal-footer">
        <button type="button" class="kt-btn kt-btn-light" [attr.data-kt-modal-dismiss]="'#' + linkModalId" [disabled]="linking()">Cancel</button>
        <button type="submit" class="kt-btn kt-btn-primary" data-testid="ps-link-submit" [disabled]="linking()">
          @if (linking()) {
            <span class="ps-spinner ps-spinner-sm" aria-hidden="true"></span>
            Linking…
          } @else {
            Link document
          }
        </button>
      </div>
    </form>
  </div>
</div>

<app-catalog-delete-confirm-modal
  title="Remove pricing supplement"
  confirmLabel="Remove"
  [modalId]="deleteModalId"
  [message]="deleteMessage"
  [confirming]="deleting()"
  (confirm)="confirmDelete()"
/>
